"use client";

// Stashes WHICH showcased build the visitor asked to clone, so the choice
// survives an interruption. The homepage's other intents (a typed prompt, a
// business URL) ride through the LoginModal on `?next=`, but a clone target
// is a project id / template slug rather than a route, and the flow can be
// dismissed, sent through a full-page OAuth redirect, restored from bfcache,
// or — since PANDA-1618 — reloaded by the email-verification gate. Any of
// those drops in-memory state. Mirrors the prompt/business stashes in
// lib/home-prompt-stash.ts.

import type { HomepageCloneSurface } from "@/lib/analytics";

const KEY = "panda:home-clone-resume";

// Deliberately much shorter than the prompt stash's hour. Resuming a clone
// spends a build credit the moment it fires, so a forgotten intent must not
// sit around and surprise the user with a charge on some later signed-in
// visit. Long enough to cover a full-page OAuth round-trip, short enough that
// "I clicked that ages ago and walked away" expires instead of firing.
const AUTH_TTL_MS = 15 * 60 * 1000;

// The email-verification round-trip is a human opening an inbox — often on a
// different device — so 15 minutes drops people who were still coming back.
// The longer window is safe because a verification-parked intent is only ever
// auto-resumed on the reload the gate modal itself triggers, or after an
// explicit "Resume" tap (see the resume paths in app/home-client.tsx); it can
// never fire unattended.
const VERIFICATION_TTL_MS = 60 * 60 * 1000;

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Template slugs are lowercase kebab-case (see /api/templates). Same threat
// model as the project id below: the value is interpolated into a request path.
const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const SLUG_MAX_LENGTH = 80;

const SURFACES = [
  "built_with_panda",
  "community_card",
  "homepage_template",
] as const satisfies readonly HomepageCloneSurface[];

/** What the visitor asked to clone. Exactly one of the two is set. */
export type CloneTarget = { projectId: string } | { templateSlug: string };

export interface CloneIntent {
  /** Fork-by-id target — "Built with Panda" and community cards. */
  projectId: string | null;
  /** Clone-by-slug target — homepage template cards. */
  templateSlug: string | null;
  surface: HomepageCloneSurface;
  /**
   * True when the email-verification gate parked this intent (as opposed to
   * the login gate). Resume paths treat the two differently: an auth intent
   * replays as soon as the user is signed in, a verification intent only on
   * the post-verification reload or an explicit tap.
   */
  pendingVerification: boolean;
}

type CloneStash = CloneIntent & { timestamp: number };

/** Reject anything that isn't a UUID: the stashed value is interpolated into
 *  `/api/projects/<id>/fork`, and localStorage is attacker-writable via any
 *  XSS on this origin. Validated on write AND read — a value stashed by an
 *  older build must not be trusted on the way out. */
function isProjectId(value: unknown): value is string {
  return typeof value === "string" && UUID_RE.test(value);
}

/** Same reasoning as isProjectId — this one lands in
 *  `/api/templates/<slug>/use`. */
function isTemplateSlug(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    value.length <= SLUG_MAX_LENGTH &&
    SLUG_RE.test(value)
  );
}

function isSurface(value: unknown): value is HomepageCloneSurface {
  return SURFACES.includes(value as HomepageCloneSurface);
}

export function stashCloneIntent(
  target: CloneTarget,
  options?: {
    surface?: HomepageCloneSurface;
    pendingVerification?: boolean;
  },
) {
  if (typeof window === "undefined") return;

  const projectId = "projectId" in target ? target.projectId : null;
  const templateSlug = "templateSlug" in target ? target.templateSlug : null;
  // Validate on the way IN as well as out, so a bad value never reaches
  // storage in the first place.
  const validProjectId = isProjectId(projectId) ? projectId : null;
  const validSlug = isTemplateSlug(templateSlug) ? templateSlug : null;
  if (!validProjectId && !validSlug) return;

  try {
    const stash: CloneStash = {
      projectId: validProjectId,
      templateSlug: validSlug,
      surface: isSurface(options?.surface)
        ? options.surface
        : "built_with_panda",
      pendingVerification: options?.pendingVerification === true,
      timestamp: Date.now(),
    };
    localStorage.setItem(KEY, JSON.stringify(stash));
  } catch (err) {
    // localStorage throws in private browsing / on quota-exceeded. The clone
    // simply won't resume; the user lands signed in on the homepage.
    console.warn("[clone-stash] save failed:", err);
  }
}

export function readCloneIntent(): CloneIntent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CloneStash>;

    const projectId = isProjectId(parsed?.projectId) ? parsed.projectId : null;
    const templateSlug = isTemplateSlug(parsed?.templateSlug)
      ? parsed.templateSlug
      : null;
    const pendingVerification = parsed?.pendingVerification === true;
    const ttl = pendingVerification ? VERIFICATION_TTL_MS : AUTH_TTL_MS;

    if (
      (!projectId && !templateSlug) ||
      typeof parsed.timestamp !== "number" ||
      Date.now() - parsed.timestamp > ttl
    ) {
      localStorage.removeItem(KEY);
      return null;
    }

    return {
      projectId,
      templateSlug,
      // Payloads written before surfaces existed came from the community-card
      // clone button, which is what "built_with_panda" now names.
      surface: isSurface(parsed.surface) ? parsed.surface : "built_with_panda",
      pendingVerification,
    };
  } catch (err) {
    console.warn("[clone-stash] read failed:", err);
    try {
      localStorage.removeItem(KEY);
    } catch {
      // ignore
    }
    return null;
  }
}

export function clearCloneIntent() {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(KEY);
  } catch (err) {
    console.warn("[clone-stash] clear failed:", err);
  }
}
